import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardCheck, ArrowRight, ArrowLeft, CheckCircle2, XCircle, RotateCcw, Clock, AlertTriangle } from 'lucide-react';
import styles from './LessonContentRenderer.module.css';
import { getQuizById, createQuizAttempt, submitQuizAttempt, getQuizAttempts } from '@/features/courses/api';
import { useAuth } from '@/features/auth';
import { useParams, useNavigate } from 'react-router-dom';
import { useTimer } from '@/app/providers/TimerContext';
import Button from '@/shared/ui/Button/Button';
import { questionMapping } from '@/features/courses/data/assessment/questionMapping';
import { questionBank } from '@/features/courses/data/assessment/questionBank';

interface QuizTriggerProps {
    quizId: string;
    onPass?: (result: any) => void;
    initialResult?: any;
    layout?: 'single' | 'scrollable';
}

type QuizView = 'briefing' | 'quiz' | 'results';

const getQuizContentConfig = (quizId: string) => {
    switch (quizId) {
        case 'review-mastery-1-5':
            return {
                briefingTitle: 'Phase 1 Review Assessment',
                briefingText: 'This assessment covers Modules 1 through 5. Complete this review to verify your foundational driving knowledge.',
                successTitle: 'Phase 1 Review Passed!',
                successText: 'Excellent work! You have mastered the foundational concepts of Modules 1-5 and are ready to proceed.',
                failureTitle: 'Phase 1 Review Required',
                failureText: "You didn't reach the required 75% score. Please review the first 5 modules and try again."
            };
        case 'review-mastery-6-10':
            return {
                briefingTitle: 'Phase 2 Review Assessment',
                briefingText: 'This assessment covers Modules 6 through 10. Complete this review to verify your advanced driving knowledge.',
                successTitle: 'Phase 2 Review Passed!',
                successText: 'Excellent work! You have mastered the advanced concepts of Modules 6-10 and are ready for the final steps.',
                failureTitle: 'Phase 2 Review Required',
                failureText: "You didn't reach the required 75% score. Please review Modules 6-10 and try again."
            };
        case 'final-exam-mastery':
            return {
                briefingTitle: 'Final Course Examination',
                briefingText: 'This is the official Fastrack Driving School final course examination. Secure a passing score of 75% or higher to graduate and qualify for your certificate.',
                successTitle: 'Final Examination Passed!',
                successText: 'Congratulations! You have successfully passed the final examination and completed the course!',
                failureTitle: 'Examination Review Required',
                failureText: 'You did not achieve the required 75% passing score. Take some time to review the curriculum before your next attempt.'
            };
        default:
            return {
                briefingTitle: 'Module Mastery Assessment',
                briefingText: "Congratulations on completing the instruction. It's time to verify your knowledge and secure your progress.",
                successTitle: 'Mastery Verified!',
                successText: "Excellent work. You've demonstrated a solid understanding of this module.",
                failureTitle: 'Review Required',
                failureText: "You didn't reach the required mastery score. Review the material and try again."
            };
    }
};

const QuizTrigger: React.FC<QuizTriggerProps> = ({ quizId, onPass, initialResult, layout = 'single' }) => {
    const { user } = useAuth();
    const { courseId } = useParams<{ courseId: string }>();
    const navigate = useNavigate();
    const config = getQuizContentConfig(quizId);

    let timerContext: any = null;
    try {
        timerContext = useTimer();
    } catch (e) {
        // Safe fallback for testing outside TimerProvider
    }
    const currentSessionId = timerContext?.currentSessionId;
    const timeUntilBreak = timerContext?.timeUntilBreak;
    const getRemainingDailyTime = timerContext?.getRemainingDailyTime;

    // Persistent cache for the duration of the page session to survive parent re-mounts
    // State management
    const [view, setView] = useState<QuizView>(() => {
        if (initialResult) return 'results';
        return (window as any).__fastrack_quiz_cache?.[quizId]?.view || 'briefing';
    });

    const [loading, setLoading] = useState(() => {
        if (initialResult) return false;
        const cached = (window as any).__fastrack_quiz_cache?.[quizId];
        if (cached?.initialized) return false;
        // Start as true only if we are likely to fetch or already fetching
        return !!(user && courseId && quizId) || !!cached?.inFlight;
    });

    const [quizData, setQuizData] = useState<any>(() => {
        return (window as any).__fastrack_quiz_cache?.[quizId]?.quizData || null;
    });
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
        return (window as any).__fastrack_quiz_cache?.[quizId]?.currentQuestionIndex || 0;
    });
    const [answers, setAnswers] = useState<Record<string, string>>(() => {
        return (window as any).__fastrack_quiz_cache?.[quizId]?.answers || {};
    });
     const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<any>(() => {
        return initialResult || (window as any).__fastrack_quiz_cache?.[quizId]?.result || null;
    });

    const [showReview, setShowReview] = useState<boolean>(() => {
        return (window as any).__fastrack_quiz_cache?.[quizId]?.showReview || false;
    });
    const [reviewLoading, setReviewLoading] = useState(false);

    const isPhaseReview = quizId === 'review-mastery-1-5' || quizId === 'review-mastery-6-10';
    const isFinalExam = quizId === 'final-exam-mastery';
    const isTimedAssessment = isPhaseReview || isFinalExam;
    const timeLimit = isFinalExam ? 3600 : isPhaseReview ? 2700 : null;

    const [timeRemaining, setTimeRemaining] = useState<number | null>(() => {
        if (!isTimedAssessment || timeLimit === null) return null;
        const cached = (window as any).__fastrack_quiz_cache?.[quizId]?.timeRemaining;
        return cached !== undefined ? cached : timeLimit;
    });

    const isMounted = React.useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const onPassRef = React.useRef(onPass);
    useEffect(() => {
        onPassRef.current = onPass;
    }, [onPass]);

    // Sync active state back to cache whenever it changes to survive parent unmounts
    useEffect(() => {
        if (!quizId) return;
        if (!(window as any).__fastrack_quiz_cache) {
            (window as any).__fastrack_quiz_cache = {};
        }
        if (!(window as any).__fastrack_quiz_cache[quizId]) {
            (window as any).__fastrack_quiz_cache[quizId] = {};
        }

        const currentCache = (window as any).__fastrack_quiz_cache[quizId];
        (window as any).__fastrack_quiz_cache[quizId] = {
            ...currentCache,
            view,
            result,
            quizData,
            currentQuestionIndex,
            answers,
            timeRemaining,
            showReview
        };
    }, [quizId, view, result, quizData, currentQuestionIndex, answers, timeRemaining, showReview]);

    useEffect(() => {
        const checkExistingAttempt = async () => {
            // 1. Initial Sanity Check
            const cache = (window as any).__fastrack_quiz_cache?.[quizId];

            if (cache?.initialized) {
                console.log(`[QuizTrigger] Using cached result for ${quizId}`);
                if (cache.result) setResult(cache.result);
                if (cache.view) setView(cache.view);
                if (cache.quizData) setQuizData(cache.quizData);
                if (cache.currentQuestionIndex !== undefined) setCurrentQuestionIndex(cache.currentQuestionIndex);
                if (cache.answers) setAnswers(cache.answers);
                if (cache.timeRemaining !== undefined) setTimeRemaining(cache.timeRemaining);
                setLoading(false);
                return;
            }

            // 2. Wait for dependencies
            if (!user || !courseId || !quizId) {
                console.log(`[QuizTrigger] Waiting for dependencies: user=${!!user}, courseId=${!!courseId}`);
                // Ensure we don't show spinner if we haven't even started
                if (loading) setLoading(false);
                return;
            }

            // 3. Prevent redundant in-flight triggers
            if (cache?.inFlight) {
                console.log(`[QuizTrigger] ${quizId} fetch already in flight, waiting...`);
                setLoading(true);
                return;
            }

            // 4. Start Fetch
            setLoading(true);
            console.log(`[QuizTrigger] Checking attempts for ${quizId} (user: ${user.uid})`);

            // Mark as in-flight globally
            if (!(window as any).__fastrack_quiz_cache) (window as any).__fastrack_quiz_cache = {};
            (window as any).__fastrack_quiz_cache[quizId] = { inFlight: true };

            try {
                // Add a small safety delay to see the spinner if debugging, or skip for production
                // await new Promise(resolve => setTimeout(resolve, 500));

                const attempts = await getQuizAttempts(user.uid, courseId, quizId);
                console.log(`[QuizTrigger] Fetched ${attempts.length} attempts for ${quizId}`);

                const completedAttempt = attempts.find(a => a.status === 'completed' || a.status === 'passed');

                let resultData: any = null;
                let viewData: QuizView = 'briefing';

                if (completedAttempt) {
                    console.log(`[QuizTrigger] Found completed attempt:`, completedAttempt);
                    resultData = {
                        score: completedAttempt.score,
                        passed: completedAttempt.passed,
                        correctAnswers: completedAttempt.correctAnswers,
                        totalQuestions: completedAttempt.totalQuestions
                    };

                    if (isMounted.current) {
                        setResult(resultData);
                        setView('results');
                        if (completedAttempt.answers) {
                            setAnswers(completedAttempt.answers);
                        }
                    }
                    viewData = 'results';

                    const isNewPass = completedAttempt.passed &&
                        (!initialResult || initialResult.score !== resultData.score);

                    if (isNewPass && onPassRef.current) {
                        console.log(`[QuizTrigger] Reporting pass to parent for ${quizId}`);
                        onPassRef.current(resultData);
                    }
                }

                // 5. Commit to Cache
                (window as any).__fastrack_quiz_cache[quizId] = {
                    ...((window as any).__fastrack_quiz_cache[quizId] || {}),
                    initialized: true,
                    inFlight: false,
                    result: resultData,
                    view: viewData,
                    answers: completedAttempt?.answers || {}
                };

            } catch (error) {
                console.error(`[QuizTrigger] Error checking attempts for ${quizId}:`, error);
                // Clear in-flight so it can retry
                (window as any).__fastrack_quiz_cache[quizId] = { inFlight: false };
            } finally {
                if (isMounted.current) {
                    console.log(`[QuizTrigger] Initialization complete for ${quizId}`);
                    setLoading(false);
                }
            }
        };

        checkExistingAttempt();

        // Safety Fallback: Clear loading if stuck too long
        const timeoutId = setTimeout(() => {
            if (isMounted.current && loading) {
                console.warn(`[QuizTrigger] Initialization timeout for ${quizId} - clearing loading state`);
                setLoading(false);
            }
        }, 8000);

        return () => clearTimeout(timeoutId);
    }, [user?.uid, courseId, quizId]);

    // Countdown Timer Decrement effect
    useEffect(() => {
        if (!isTimedAssessment || view !== 'quiz' || timeRemaining === null || timeRemaining <= 0 || submitting) return;

        const interval = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev === null) return null;
                const nextVal = prev - 1;
                if (nextVal <= 0) {
                    clearInterval(interval);
                    return 0;
                }
                return nextVal;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isTimedAssessment, view, timeRemaining === 0, submitting]);


    // Auto-submission effect when timer expires
    useEffect(() => {
        if (isTimedAssessment && timeRemaining === 0 && view === 'quiz' && !submitting) {
            handleSubmit();
        }
    }, [isTimedAssessment, timeRemaining, view, submitting]);

    const handleStart = async () => {
        if (!courseId || !user) return;

        if (quizId === 'final-exam-mastery') {
            console.log('[QuizTrigger] Intercepting final exam launch for PVQ check...');
            
            const event = new CustomEvent('fastrack:trigger-pvq', {
                detail: {
                    reason: 'final-exam',
                    onSuccess: async () => {
                        console.log('[QuizTrigger] PVQ verified successfully! Initializing Final Exam...');
                        if (!isMounted.current) return;
                        setLoading(true);
                        try {
                            const data = await getQuizById(courseId, quizId);
                            if (isMounted.current) {
                                setQuizData(data);
                                setView('quiz');
                            }
                        } catch (error) {
                            console.error('Failed to load quiz data:', error);
                        } finally {
                            if (isMounted.current) setLoading(false);
                        }
                    },
                    onFailure: () => {
                        console.warn('[QuizTrigger] PVQ verification cancelled or failed.');
                    }
                }
            });
            
            window.dispatchEvent(event);
            return;
        }

        setLoading(true);
        console.log(`[QuizTrigger] Starting quiz ${quizId}`);
        try {
            const data = await getQuizById(courseId, quizId);
            if (isMounted.current) {
                setQuizData(data);
                setView('quiz');
            }
        } catch (error) {
            console.error('Failed to load quiz data:', error);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    };

    const handleAnswer = (questionId: string, answer: string) => {
        setAnswers(prev => ({ ...prev, [questionId]: answer }));
    };

    const handleNext = () => {
        if (quizData?.questions && currentQuestionIndex < quizData.questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const handleSubmit = async () => {
        if (!user || !courseId || !quizData) return;
        setSubmitting(true);
        try {
            // 1. Create Attempt
            const attemptId = await createQuizAttempt(user.uid, courseId, {
                quizId: quizData.id,
                quizTitle: quizData.title,
                isFinalExam: false,
                sessionId: 'inline-session',
                ipAddress: null,
                deviceInfo: navigator.userAgent
            });

            // 2. Submit Attempt
            const submissionResult = await submitQuizAttempt(user.uid, attemptId, {
                answers,
                totalQuestions: quizData.questions.length,
                correctAnswers: quizData.questions.filter((q: any) => {
                    const ans = answers[q.id];
                    const correct = typeof q.correctAnswer === 'number'
                        ? (q.options?.[q.correctAnswer] || q.answers?.[q.correctAnswer])
                        : q.correctAnswer;
                    return ans === correct;
                }).length,
                timeSpent: isTimedAssessment && timeRemaining !== null && timeLimit !== null
                    ? Math.min(timeLimit, timeLimit - timeRemaining)
                    : 0
            });

            if (isMounted.current) {
                setResult(submissionResult);
                setView('results');
                if (submissionResult.passed) {
                    // Call the final exam tracking Cloud Function to record user exam pass and auto-generate completion certificate
                    if (quizId === 'final-exam-mastery') {
                        try {
                            const { getFunctionsInstance } = await import('@lib/firebase');
                            const { httpsCallable } = await import('firebase/functions');
                            const functions = getFunctionsInstance();
                            const trackExamAttemptFn = httpsCallable(functions, 'trackExamAttempt');
                            console.log('[QuizTrigger] Invoking trackExamAttempt Cloud Function to record pass and generate certificate...', {
                                userId: user.uid,
                                courseId,
                                sessionId: currentSessionId || 'inline-session',
                                score: submissionResult.correctAnswers,
                                totalQuestions: submissionResult.totalQuestions
                            });
                            const certResult = await trackExamAttemptFn({
                                userId: user.uid,
                                courseId,
                                sessionId: currentSessionId || 'inline-session',
                                score: submissionResult.correctAnswers,
                                totalQuestions: submissionResult.totalQuestions
                            });
                            console.log('[QuizTrigger] trackExamAttempt response:', certResult.data);
                        } catch (certError: any) {
                            console.error('[QuizTrigger] Failed to auto-generate completion certificate:', certError);
                        }
                    }
                    onPassRef.current?.(submissionResult);
                }
            }
        } catch (error) {
            console.error('Failed to submit quiz:', error);
        } finally {
            if (isMounted.current) setSubmitting(false);
        }
    };

    const handleRetry = async () => {
        setAnswers({});
        setCurrentQuestionIndex(0);
        setResult(null);
        setShowReview(false);
        if (isTimedAssessment && timeLimit !== null) {
            setTimeRemaining(timeLimit);
        }
        if (!quizData) {
            if (!courseId || !user) return;
            setLoading(true);
            try {
                const data = await getQuizById(courseId, quizId);
                if (isMounted.current) {
                    setQuizData(data);
                    setView('quiz');
                }
            } catch (error) {
                console.error('Failed to load quiz data for retry:', error);
            } finally {
                if (isMounted.current) setLoading(false);
            }
        } else {
            setView('quiz');
        }
    };

    const handleToggleReview = async () => {
        if (showReview) {
            setShowReview(false);
            return;
        }

        if (!quizData) {
            if (!courseId || !user) return;
            setReviewLoading(true);
            try {
                const data = await getQuizById(courseId, quizId);
                if (isMounted.current) {
                    setQuizData(data);
                    setShowReview(true);
                }
            } catch (error) {
                console.error('Failed to load quiz data for review:', error);
            } finally {
                if (isMounted.current) setReviewLoading(false);
            }
        } else {
            setShowReview(true);
        }
    };

    const renderWarnings = () => {
        if (!timerContext) return null;

        const isPhaseReview = quizId === 'review-mastery-1-5' || quizId === 'review-mastery-6-10';
        const isFinalExam = quizId === 'final-exam-mastery';
        const isTimedAssessment = isPhaseReview || isFinalExam;

        // 1. Daily Lockout warning calculations
        const remainingDailyTime = typeof getRemainingDailyTime === 'function' ? getRemainingDailyTime() : null;
        if (remainingDailyTime !== null) {
            const dailyThreshold = isFinalExam ? 3600 : isPhaseReview ? 2700 : 600; // 60m for final, 45m for phase review, 10m for standard
            if (remainingDailyTime < dailyThreshold) {
                const remainingMinutes = Math.ceil(remainingDailyTime / 60);
                const requiredMinutes = isFinalExam ? 60 : isPhaseReview ? 45 : 10;
                
                return (
                    <motion.div 
                        className={styles.warningCard}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <AlertTriangle className={styles.warningCardIcon} size={24} />
                        <div className={styles.warningCardText}>
                            <h4>⚠️ Daily Limit Lockout Warning</h4>
                            {isTimedAssessment ? (
                                <p>
                                    You have only <strong>{remainingMinutes} minutes</strong> remaining of your 4-hour daily training limit today. Since this exam requires up to <strong>{requiredMinutes} minutes</strong> to complete, you will hit your daily lockout before you can finish. We strongly recommend completing this exam tomorrow.
                                </p>
                            ) : (
                                <p>
                                    You have only <strong>{remainingMinutes} minutes</strong> remaining of your 4-hour daily training limit. We recommend waiting until tomorrow or after midnight to start this assessment so your progress is not interrupted by a daily lockout.
                                </p>
                            )}
                        </div>
                    </motion.div>
                );
            }
        }

        // 2. Break Warning calculations (if daily lockout is not triggered)
        if (timeUntilBreak !== undefined && timeUntilBreak !== null) {
            const breakThreshold = isTimedAssessment ? 1800 : 600; // 30m for timed exams, 10m for standard quizzes
            if (timeUntilBreak < breakThreshold) {
                const remainingMinutes = Math.ceil(timeUntilBreak / 60);
                
                return (
                    <motion.div 
                        className={styles.warningCard}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Clock className={styles.warningCardIcon} size={24} />
                        <div className={styles.warningCardText}>
                            <h4>⚠️ Upcoming Compliance Break</h4>
                            {isTimedAssessment ? (
                                <p>
                                    A mandatory 10-minute break is scheduled in <strong>{remainingMinutes} minutes</strong>. Since this is a timed high-stakes assessment, we strongly recommend completing your break first to avoid mid-exam lockout.
                                </p>
                            ) : (
                                <p>
                                    A mandatory 10-minute break is scheduled in <strong>{remainingMinutes} minutes</strong>. We recommend waiting until after your break to start this assessment so your learning progress is not interrupted.
                                </p>
                            )}
                        </div>
                    </motion.div>
                );
            }
        }

        return null;
    };

    const renderBriefing = () => (
        <motion.div
            className={styles.quizTriggerInner}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <div className={styles.quizTriggerContent}>
                <div className={styles.quizTriggerIcon}>
                    <ClipboardCheck size={40} />
                </div>
                <div className={styles.quizTriggerText}>
                    <h3>{config.briefingTitle}</h3>
                    <p>{config.briefingText}</p>
                </div>
            </div>

            {/* Warn-Only Compliance Banners */}
            {renderWarnings()}

            <button
                className={styles.quizTriggerButton}
                onClick={handleStart}
                disabled={loading}
            >
                {loading ? 'Preparing...' : 'Begin Assessment'} <ArrowRight size={20} />
            </button>
        </motion.div>
    );

    const renderTimer = () => {
        if (!isTimedAssessment || timeRemaining === null || timeLimit === null) return null;

        const minutes = Math.floor(timeRemaining / 60);
        const seconds = timeRemaining % 60;
        const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        let timerColorClass = styles.timerNormal;
        let timerLabel = 'Time Remaining';

        if (timeRemaining <= 300) {
            timerColorClass = styles.timerDanger;
            timerLabel = '⚠️ Auto-submitting in:';
        }

        const percentage = (timeRemaining / timeLimit) * 100;

        return (
            <div className={`${styles.timerContainer} ${timerColorClass}`} style={{ marginBottom: '1.5rem' }}>
                <div className={styles.timerHeader}>
                    <span className={styles.timerLabel}>{timerLabel}</span>
                    <span className={styles.timerClock}>{formattedTime}</span>
                </div>
                <div className={styles.timerBarWrapper}>
                    <div 
                        className={styles.timerBar} 
                        style={{ width: `${percentage}%` }}
                    />
                </div>
            </div>
        );
    };

    const renderQuiz = () => {
        if (!quizData || !quizData.questions || quizData.questions.length === 0) {
            return (
                <div className={styles.quizTriggerActive}>
                    <p>Loading questions or no questions available...</p>
                </div>
            );
        }

        if (layout === 'scrollable') {
            const answeredCount = Object.keys(answers).length;
            const totalQuestions = quizData.questions.length;
            const progress = (answeredCount / totalQuestions) * 100;
            const allAnswered = answeredCount === totalQuestions;

            const showExpiredOverlay = isTimedAssessment && timeRemaining === 0;

            return (
                <motion.div
                    className={styles.scrollableQuizContainer}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    style={{ position: 'relative' }}
                >
                    <AnimatePresence>
                        {showExpiredOverlay && (
                            <motion.div
                                className={styles.timerExpiredOverlay}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <Clock className={styles.spinning} size={48} />
                                <h4>Time Expired!</h4>
                                <p>Auto-submitting your answers...</p>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {renderTimer()}
                    <div className={styles.quizProgressHeader}>
                        <div className={styles.quizProgressInfo}>
                            <span>Progress</span>
                            <span>{answeredCount} of {totalQuestions} answered ({Math.round(progress)}%)</span>
                        </div>
                        <div className={styles.quizProgressBarContainer}>
                            <div className={styles.quizProgressBar} style={{ width: `${progress}%` }} />
                        </div>
                    </div>

                    {quizData.questions.map((question: any, idx: number) => {
                        const currentAnswer = answers[question.id];
                        return (
                            <div key={question.id} className={styles.scrollableQuestionBlock}>
                                <div className={styles.scrollableQuestionHeader}>
                                    <span className={styles.scrollableQuestionNumber}>
                                        Question {idx + 1}
                                    </span>
                                    <h4 className={styles.scrollableQuestionText}>
                                        {question.text || question.question}
                                    </h4>
                                </div>
                                <div className={styles.quizOptionsGrid}>
                                    {(question.answers || question.options).map((option: string, i: number) => (
                                        <button
                                            key={i}
                                            className={`${styles.quizOptionButton} ${currentAnswer === option ? styles.selected : ''}`}
                                            onClick={() => handleAnswer(question.id, option)}
                                            disabled={submitting || timeRemaining === 0}
                                        >
                                            <span className={styles.optionIndex}>{String.fromCharCode(65 + i)}</span>
                                            <span className={styles.optionText}>{option}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}

                    <div className={styles.scrollableSubmitSection}>
                        {!allAnswered && (
                            <span className={styles.scrollableSubmitFeedback}>
                                Please answer all {totalQuestions} questions to submit. ({totalQuestions - answeredCount} remaining)
                            </span>
                        )}
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={!allAnswered || submitting}
                            loading={submitting}
                            size="large"
                        >
                            Submit Assessment
                        </Button>
                    </div>
                </motion.div>
            );
        }

        const question = quizData.questions[currentQuestionIndex];
        if (!question) return null;

        const isLastQuestion = currentQuestionIndex === quizData.questions.length - 1;
        const currentAnswer = answers[question.id];
        const progress = ((currentQuestionIndex + 1) / quizData.questions.length) * 100;

        const showExpiredOverlay = isTimedAssessment && timeRemaining === 0;

        return (
            <motion.div
                className={styles.quizTriggerActive}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                style={{ position: 'relative' }}
            >
                <AnimatePresence>
                    {showExpiredOverlay && (
                        <motion.div
                            className={styles.timerExpiredOverlay}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <Clock className={styles.spinning} size={48} />
                            <h4>Time Expired!</h4>
                            <p>Auto-submitting your answers...</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {renderTimer()}
                <div className={styles.quizProgressHeader}>
                    <div className={styles.quizProgressInfo}>
                        <span>Question {currentQuestionIndex + 1} of {quizData.questions.length}</span>
                        <span>{Math.round(progress)}% Complete</span>
                    </div>
                    <div className={styles.quizProgressBarContainer}>
                        <div className={styles.quizProgressBar} style={{ width: `${progress}%` }} />
                    </div>
                </div>

                <div className={styles.quizQuestionSpace}>
                    <h4 className={styles.quizQuestionText}>{question.text || question.question}</h4>
                    <div className={styles.quizOptionsGrid}>
                        {(question.answers || question.options).map((option: string, i: number) => (
                            <button
                                key={i}
                                className={`${styles.quizOptionButton} ${currentAnswer === option ? styles.selected : ''}`}
                                onClick={() => handleAnswer(question.id, option)}
                                disabled={submitting || timeRemaining === 0}
                            >
                                <span className={styles.optionIndex}>{String.fromCharCode(65 + i)}</span>
                                <span className={styles.optionText}>{option}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className={styles.quizNavActions}>
                    <Button variant="ghost" onClick={handlePrev} disabled={currentQuestionIndex === 0}>
                        <ArrowLeft size={18} /> Previous
                    </Button>
                    {isLastQuestion ? (
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={Object.keys(answers).length < (quizData?.questions?.length || 0) || submitting}
                            loading={submitting}
                        >
                            Submit Assessment
                        </Button>
                    ) : (
                        <Button variant="primary" onClick={handleNext} disabled={!currentAnswer}>
                            Next <ArrowRight size={18} />
                        </Button>
                    )}
                </div>
            </motion.div>
        );
    };

    const renderResults = () => {
        if (!result) return null;

        return (
            <motion.div
                className={styles.quizTriggerResults}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
            >
                <div className={styles.resultHeader}>
                    {result.passed ? (
                        <div className={styles.resultSuccess}>
                            <CheckCircle2 size={48} />
                            <h3>{config.successTitle}</h3>
                            <p>{config.successText}</p>
                        </div>
                    ) : (
                        <div className={styles.resultFailure}>
                            <XCircle size={48} />
                            <h3>{config.failureTitle}</h3>
                            <p>{config.failureText}</p>
                        </div>
                    )}
                </div>

                <div className={result.passed ? styles.resultScoreCard : styles.resultScoreCard}>
                    <div className={styles.scoreItem}>
                        <span className={styles.scoreLabel}>Your Score</span>
                        <span className={`${styles.scoreValue} ${result.passed ? styles.passed : styles.failed}`}>
                            {result.score}%
                        </span>
                    </div>
                    <div className={styles.scoreItem}>
                        <span className={styles.scoreLabel}>Status</span>
                        <span className={`${styles.statusBadge} ${result.passed ? styles.passed : styles.failed}`}>
                            {result.passed ? 'PASSED' : 'RETRY'}
                        </span>
                    </div>
                </div>

                <div className={styles.resultActions} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {quizId === 'final-exam-mastery' && result.passed && (
                        <Button
                            variant="primary"
                            onClick={() => navigate('/dashboard?tab=certificates')}
                            className={styles.retryButton}
                        >
                            Claim Completion Certificate 🎉
                        </Button>
                    )}
                    {(!result || (quizId !== 'final-exam-mastery' && result.score < 100) || (quizId === 'final-exam-mastery' && !result.passed)) && (
                        <Button
                            variant="primary"
                            onClick={handleRetry}
                            className={styles.retryButton}
                        >
                            {result?.passed ? 'Improve Score' : 'Try Again'}
                        </Button>
                    )}
                    {result && result.score < 100 && (
                        <Button
                            variant="outline"
                            onClick={handleToggleReview}
                            disabled={reviewLoading}
                            loading={reviewLoading}
                            className={styles.retryButton}
                        >
                            {showReview ? 'Hide Study Review' : 'Review Study Concepts'}
                        </Button>
                    )}
                </div>

                {showReview && (answers || quizData?.questions) && (() => {
                    const letterToIndex = (val: any): number => {
                        if (typeof val === 'number') return val;
                        if (typeof val !== 'string') return -1;
                        const trimmed = val.trim();
                        const upper = trimmed.toUpperCase();

                        // Match single letter A, B, C, or D
                        if (/^[A-D]$/.test(upper)) {
                            return upper.charCodeAt(0) - 65;
                        }

                        // Match optionA, Option B, OPTION_C, etc.
                        const optionMatch = upper.match(/^OPTION[\s_-]*([A-D])$/);
                        if (optionMatch) {
                            return optionMatch[1].charCodeAt(0) - 65;
                        }

                        // Match single numeric index "0", "1", "2", "3"
                        if (/^[0-3]$/.test(trimmed)) {
                            return parseInt(trimmed, 10);
                        }

                        return -1;
                    };

                    const getSelectedAnswer = (question: any, originalIndex: number): any => {
                        if (!answers) return undefined;
                        if (question.id && answers[question.id] !== undefined) return answers[question.id];
                        if (answers[originalIndex] !== undefined) return answers[originalIndex];
                        if (answers[String(originalIndex)] !== undefined) return answers[String(originalIndex)];
                        return undefined;
                    };

                    const checkIsQuestionCorrect = (question: any, selectedAnswer: any): boolean => {
                        if (selectedAnswer === undefined || selectedAnswer === null || selectedAnswer === '') {
                            return false;
                        }

                        const options: string[] = question.options || question.answers || [];
                        const studentIndex = letterToIndex(selectedAnswer);
                        const correctRaw = question.correctAnswer;
                        const correctIndex = letterToIndex(correctRaw);

                        const studentText = typeof selectedAnswer === 'string' && studentIndex === -1
                            ? selectedAnswer.trim()
                            : (options[studentIndex] || String(selectedAnswer).trim());

                        const correctText = typeof correctRaw === 'string' && correctIndex === -1
                            ? correctRaw.trim()
                            : (options[correctIndex] || String(correctRaw).trim());

                        if (studentIndex >= 0 && correctIndex >= 0 && studentIndex === correctIndex) {
                            return true;
                        }

                        if (studentText && correctText && studentText.toLowerCase() === correctText.toLowerCase()) {
                            return true;
                        }

                        if (selectedAnswer === correctRaw || String(selectedAnswer) === String(correctRaw)) {
                            return true;
                        }

                        return false;
                    };

                    // Construct the question pool for study review:
                    // Primary source: look up every question ID answered in student's attempt via central questionBank
                    // Fallback: quizData.questions if answers is unavailable or question ID not found
                    let reviewQuestionsPool: any[] = [];
                    if (answers && Object.keys(answers).length > 0) {
                        const answeredKeys = Object.keys(answers);
                        const resolvedFromBank = answeredKeys
                            .map((qId) => {
                                const bankQ = questionBank.find((q: any) => q.id === qId);
                                if (bankQ) return bankQ;
                                return quizData?.questions?.find((q: any) => q.id === qId);
                            })
                            .filter(Boolean);

                        if (resolvedFromBank.length > 0) {
                            reviewQuestionsPool = resolvedFromBank;
                        } else {
                            reviewQuestionsPool = quizData?.questions || [];
                        }
                    } else {
                        reviewQuestionsPool = quizData?.questions || [];
                    }

                    const missedQuestions = reviewQuestionsPool
                        .map((question: any, originalIndex: number) => {
                            const selectedAnswer = getSelectedAnswer(question, originalIndex);
                            const isCorrect = checkIsQuestionCorrect(question, selectedAnswer);

                            return {
                                question,
                                originalIndex,
                                selectedAnswer,
                                isCorrect
                            };
                        })
                        .filter((item: any) => !item.isCorrect);

                    return (
                        <div className={styles.studyReviewContainer}>
                            <div className={styles.studyReviewHeader}>
                                <h4>📖 Study Review</h4>
                                <p>Below are the concept areas you missed on this attempt ({missedQuestions.length} topic{missedQuestions.length === 1 ? '' : 's'}). Review the lesson material for these topics before your next attempt.</p>
                            </div>
                            <div className={styles.studyReviewList}>
                                {missedQuestions.length === 0 ? (
                                    <div className={styles.studyReviewNote}>
                                        🎉 Excellent! You got every question right on this attempt. No study review required!
                                    </div>
                                ) : (
                                    missedQuestions.map((item: any, reviewIdx: number) => {
                                        const { question, originalIndex } = item;
                                        const category = question.category || "General Safety";
                                        const mapping = questionMapping[question.id];
                                        
                                        return (
                                            <div key={question.id || `missed-${originalIndex}`} className={styles.studyReviewItem}>
                                                <div className={styles.studyReviewItemHeader}>
                                                    <span className={styles.studyReviewLabel}>
                                                        Missed Concept {reviewIdx + 1} of {missedQuestions.length} • Exam Q#{originalIndex + 1} ({category})
                                                    </span>
                                                </div>
                                                <p className={styles.studyReviewQuestionText}>
                                                    {question.text || question.question}
                                                </p>
                                                {mapping ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                        <div className={styles.studyReviewLocation}>
                                                            <span>This topic is taught in: </span>
                                                            <strong>{mapping.moduleTitle}</strong>
                                                            <span> &rarr; </span>
                                                            <strong>{mapping.lessonTitle}</strong>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className={styles.studyReviewLink}
                                                            onClick={() => {
                                                                console.log(`[QuizTrigger] Dispatching navigation event for lesson ${mapping.lessonId}`);
                                                                window.dispatchEvent(new CustomEvent('fastrack:navigate-to-lesson', {
                                                                    detail: {
                                                                        moduleId: mapping.moduleId,
                                                                        lessonId: mapping.lessonId
                                                                    }
                                                                }));
                                                            }}
                                                        >
                                                            Go to Lesson &rarr;
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className={styles.studyReviewNote}>
                                                        Please review the lesson material for this concept.
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    );
                })()}
            </motion.div>
        );
    };

    if (loading && view === 'briefing') {
        return (
            <div className={styles.quizTriggerCard}>
                <div className={styles.quizTriggerContent}>
                    <div className={styles.loadingSpinner}>
                        <RotateCcw className={styles.spinning} size={32} />
                    </div>
                    <div className={styles.quizTriggerText}>
                        <h3>Initializing Assessment</h3>
                        <p>Securing your progress and preparing mastery verification...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.quizTriggerCard}>

            <AnimatePresence mode="wait">
                {view === 'briefing' && renderBriefing()}
                {view === 'quiz' && renderQuiz()}
                {view === 'results' && renderResults()}
            </AnimatePresence>
        </div>
    );
};

export default QuizTrigger;
